// Buraxılış imtahan sual strukturu - tək mənbə
// Xarici dil fənni ayrı-ayrı dillərə bölünür (assignment/yoxlama üçün)
// Ancaq hər şagirdin yalnız BİR xdil-i var - getEffectiveBuraxilisQuestionsForStudent istifadə edin
//
// Grade 11 blank seçimi:
//   - 2026-09-01-dən əvvəl (imtahan/yükləmə vaxtı) → köhnə Form 11
//   - 2026-09-01 və sonrası → Grade 10 blank (Q26 / Q42… / Q75…)
// Əlavə manual switch lazım deyil: exam.grade11BlankScheme və ya createdAt/uploaded_at kifayətdir.

export interface BuraxilisSubjectQuestions {
  subject: string;
  questions: string[];
}

export type BuraxilisQuestions = {
  [grade: number]: BuraxilisSubjectQuestions[];
};

/** Grade 11 blank layout */
export type Grade11BlankScheme = 'old' | 'new';

export function isGrade11BlankScheme(value: unknown): value is Grade11BlankScheme {
  return value === 'old' || value === 'new';
}

/**
 * Bu tarixdən (UTC) etibarən 11-ci sinif = 10 blank.
 * Əvvəlki imtahan/yükləmələr köhnə Form 11 saxlayır.
 */
export const GRADE11_NEW_BLANK_CUTOFF_MS = Date.UTC(2026, 8, 1); // 2026-09-01T00:00:00.000Z

/** Köhnə Form 11 açıq sual siyahısı */
export const OLD_BURAXILIS_QUESTIONS_GRADE11: BuraxilisSubjectQuestions[] = [
  { subject: 'İngilis dili', questions: ['Q4', 'Q5', 'Q6', 'Q27', 'Q28', 'Q29', 'Q30'] },
  { subject: 'Rus dili', questions: ['Q4', 'Q5', 'Q6', 'Q27', 'Q28', 'Q29', 'Q30'] },
  { subject: 'Alman dili', questions: ['Q4', 'Q5', 'Q6', 'Q27', 'Q28', 'Q29', 'Q30'] },
  { subject: 'Fransız dili', questions: ['Q4', 'Q5', 'Q6', 'Q27', 'Q28', 'Q29', 'Q30'] },
  { subject: 'Azərbaycan dili', questions: ['Q46', 'Q47', 'Q48', 'Q49', 'Q50', 'Q56', 'Q57', 'Q58', 'Q59', 'Q60'] },
  { subject: 'Riyaziyyat', questions: ['Q79', 'Q80', 'Q81', 'Q82', 'Q83', 'Q84', 'Q85'] },
];

const GRADE10_QUESTIONS: BuraxilisSubjectQuestions[] = [
  { subject: 'İngilis dili', questions: ['Q26'] },
  { subject: 'Rus dili', questions: ['Q26'] },
  { subject: 'Alman dili', questions: ['Q26'] },
  { subject: 'Fransız dili', questions: ['Q26'] },
  { subject: 'Azərbaycan dili', questions: ['Q42', 'Q43', 'Q44', 'Q45', 'Q46', 'Q52', 'Q53', 'Q54', 'Q55', 'Q56'] },
  { subject: 'Riyaziyyat', questions: ['Q75', 'Q76', 'Q77', 'Q78', 'Q79', 'Q80', 'Q81'] },
];

function cloneSubjects(list: BuraxilisSubjectQuestions[]): BuraxilisSubjectQuestions[] {
  return list.map((s) => ({ subject: s.subject, questions: [...s.questions] }));
}

/** Default catalog (grade 11 = new / grade-10 blank). Prefer getBuraxilisSubjectsForGrade + context. */
export const BURAXILIS_QUESTIONS: BuraxilisQuestions = {
  11: cloneSubjects(GRADE10_QUESTIONS),
  10: cloneSubjects(GRADE10_QUESTIONS),
  9: [
    { subject: 'İngilis dili', questions: ['Q26'] },
    { subject: 'Rus dili', questions: ['Q26'] },
    { subject: 'Alman dili', questions: ['Q26'] },
    { subject: 'Fransız dili', questions: ['Q26'] },
    { subject: 'Azərbaycan dili', questions: ['Q45', 'Q46', 'Q55', 'Q56'] },
    { subject: 'Riyaziyyat', questions: ['Q78', 'Q79', 'Q80', 'Q81'] },
  ],
  8: [
    { subject: 'İngilis dili', questions: ['Q26'] },
    { subject: 'Rus dili', questions: ['Q26'] },
    { subject: 'Alman dili', questions: ['Q26'] },
    { subject: 'Fransız dili', questions: ['Q26'] },
    { subject: 'Azərbaycan dili', questions: ['Q45', 'Q46', 'Q55', 'Q56'] },
    { subject: 'Riyaziyyat', questions: ['Q78', 'Q79', 'Q80', 'Q81'] },
  ],
};

export const FOREIGN_LANGUAGES = ['İngilis dili', 'Rus dili', 'Alman dili', 'Fransız dili'];

/** Normalize xdil/fenn to full form: "İngilis" -> "İngilis dili" */
export function normalizeXdilToFull(xdil: string | null | undefined): string {
  if (!xdil) return '';
  const normalized = xdil.trim();
  if (normalized === 'İngilis' || normalized === 'ingilis' || normalized === 'İNGİLİS') return 'İngilis dili';
  if (normalized === 'Fransız' || normalized === 'fransız' || normalized === 'FRANSIZ') return 'Fransız dili';
  if (normalized === 'Alman' || normalized === 'alman' || normalized === 'ALMAN') return 'Alman dili';
  if (normalized === 'Rus' || normalized === 'rus' || normalized === 'RUS') return 'Rus dili';
  return normalized;
}

/** Firestore Timestamp | Date | number | ISO string → ms, else null (invalid → null → 'new' fallback) */
export function toEpochMs(value: unknown): number | null {
  if (value == null || value === '') return null;

  let ms: number | null = null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    ms = value < 1e12 ? value * 1000 : value;
  } else if (value instanceof Date) {
    const t = value.getTime();
    ms = Number.isFinite(t) ? t : null;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'Invalid Date' || trimmed === 'null' || trimmed === 'undefined') {
      return null;
    }
    // Numeric string (seconds or ms)
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) return null;
      ms = n < 1e12 ? n * 1000 : n;
    } else {
      const t = Date.parse(trimmed);
      ms = Number.isFinite(t) ? t : null;
    }
  } else if (typeof value === 'object') {
    const v = value as { toMillis?: () => number; toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof v.toMillis === 'function') {
      try {
        const t = v.toMillis();
        ms = Number.isFinite(t) ? t : null;
      } catch {
        ms = null;
      }
    }
    if (ms == null && typeof v.toDate === 'function') {
      try {
        const t = v.toDate().getTime();
        ms = Number.isFinite(t) ? t : null;
      } catch {
        ms = null;
      }
    }
    if (ms == null) {
      const seconds = v.seconds ?? v._seconds;
      if (typeof seconds === 'number' && Number.isFinite(seconds)) {
        ms = seconds * 1000;
      }
    }
  }

  // Plausible exam range: 2015–01–01 … 2100–01–01. Outside → treat as missing → 'new'.
  if (ms == null || !Number.isFinite(ms)) return null;
  const MIN_MS = Date.UTC(2015, 0, 1);
  const MAX_MS = Date.UTC(2100, 0, 1);
  if (ms < MIN_MS || ms > MAX_MS) return null;
  return ms;
}

export interface BuraxilisBlankContext {
  /** Explicit frozen scheme (exam or assignment) — highest priority */
  scheme?: Grade11BlankScheme | null;
  /** Direct timestamp override */
  asOf?: unknown;
  exam?: {
    grade11BlankScheme?: Grade11BlankScheme | null;
    createdAt?: unknown;
  } | null;
  student?: {
    uploaded_at?: unknown;
    scanned_at?: unknown;
  } | null;
}

/** Build blank context from exam (+ optional student upload times). */
export function blankCtxFromExam(
  exam?: BuraxilisBlankContext['exam'],
  student?: { uploaded_at?: unknown; scanned_at?: unknown } | null
): BuraxilisBlankContext {
  return {
    exam: exam ?? null,
    student: student ?? undefined,
  };
}

/**
 * Decide grade-11 blank scheme once; callers should not re-implement cutoff logic.
 *
 * Priority: explicit scheme → exam.grade11BlankScheme → asOf/exam.createdAt/student upload
 * Fallback (missing / corrupt / unreadable timestamp): always 'new' (grade-10 blank).
 */
export function resolveGrade11BlankScheme(ctx?: BuraxilisBlankContext | Grade11BlankScheme | null): Grade11BlankScheme {
  if (isGrade11BlankScheme(ctx)) return ctx;
  if (!ctx) return 'new';

  const explicit = ctx.scheme || ctx.exam?.grade11BlankScheme;
  if (isGrade11BlankScheme(explicit)) return explicit;

  const candidates = [
    ctx.asOf,
    ctx.exam?.createdAt,
    ctx.student?.uploaded_at,
    ctx.student?.scanned_at,
  ];

  for (const candidate of candidates) {
    const ms = toEpochMs(candidate);
    if (ms == null) continue;
    return ms >= GRADE11_NEW_BLANK_CUTOFF_MS ? 'new' : 'old';
  }

  // Timestamp yoxdur və ya oxuna bilmədi → yeni blank
  return 'new';
}

/** Derive scheme to store on new exam documents at creation time */
export function grade11BlankSchemeForTimestamp(asOf: unknown = Date.now()): Grade11BlankScheme {
  return resolveGrade11BlankScheme({ asOf });
}

/**
 * Full subject/question config for a grade, respecting grade-11 blank scheme.
 */
export function getBuraxilisSubjectsForGrade(
  grade: number,
  ctx?: BuraxilisBlankContext | Grade11BlankScheme | null
): BuraxilisSubjectQuestions[] {
  if (grade === 11) {
    return resolveGrade11BlankScheme(ctx) === 'old'
      ? cloneSubjects(OLD_BURAXILIS_QUESTIONS_GRADE11)
      : cloneSubjects(GRADE10_QUESTIONS);
  }
  return BURAXILIS_QUESTIONS[grade] ? cloneSubjects(BURAXILIS_QUESTIONS[grade]) : [];
}

/**
 * Returns effective Buraxılış questions for a student (xdil-filtered).
 * Pass exam/student context so grade 11 picks old vs new blank automatically.
 */
export function getEffectiveBuraxilisQuestionsForStudent(
  grade: number,
  xdil?: string | null,
  ctx?: BuraxilisBlankContext | Grade11BlankScheme | null
): BuraxilisSubjectQuestions[] {
  const config = getBuraxilisSubjectsForGrade(grade, ctx);
  if (!config.length) return [];

  const studentXdil = normalizeXdilToFull(xdil) || 'İngilis dili';

  return config.filter((subj) => {
    if (FOREIGN_LANGUAGES.includes(subj.subject)) {
      return subj.subject === studentXdil;
    }
    return true;
  });
}
